import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Typography, Grid, Skeleton } from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { CRPoliciesSaveMappings } from './CRPoliciesSaveMappings.jsx';
import CustomTable from '../../../components/common/CustomTable/index.jsx';
import { useSelection } from '../../../hooks/use-selection.js';
import { styles } from '../styles';
import { columns as mappedPositionColumns } from './columnsMappedPosition.jsx';
import { columns as positionsColumns } from './columnsPositions';
import {
    useGetPositionsMappedToChangeRequestPolicyMutation,
    useBulkUpdatePositionChangeRequestPolicyMappingsMutation,
} from '../../../api/changeRequestPolicies';
import { useGetPositionsMutation } from '../../../api/positions';
import {
    onPageChange,
    onPageSizeChange,
    onSortChange,
    resetController,
} from '../../../store/slices/tableController.js';

const ViewUpdateMappedPositions = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const params = useParams();
    const { crPolicyId } = params;
    const isUpdateMode = location.pathname.includes('update');
    const [updatePolicyMappings, setUpdatePolicyMappings] = useState(isUpdateMode);
    const [positionsMapped, setPositionsMapped] = useState(null);
    const controller = useSelector((state) => state.tableController);
    const { enqueueSnackbar } = useSnackbar();

    const [getPositions, { isLoading: isLoadingPositions, data: listPositions }] = useGetPositionsMutation();
    const [getPositionsMappedToChangeRequestPolicy, { isLoading: isLoadingPositionsMapped }] =
        useGetPositionsMappedToChangeRequestPolicyMutation();
    const [bulkUpdatePositionChangeRequestPolicyMappings] = useBulkUpdatePositionChangeRequestPolicyMappingsMutation();

    const [selectedPositions, handleSelect, handleSelectAll, handleClearSelected] = useSelection(
        listPositions?.positionInstanceList,
    );

    const fetchPositionsMapped = async () => {
        try {
            const query = new URLSearchParams({});
            query.append('offset', controller.page * controller.pageSize);
            query.append('max', controller.pageSize);

            const response = await getPositionsMappedToChangeRequestPolicy({
                data: { id: crPolicyId },
                query,
            }).unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            setPositionsMapped({ ...response, name: response?.['mappedPositions']?.[0]?.policy?.name });
        } catch (error) {
            if (error.status === 404) enqueueSnackbar(error.data.message, { variant: 'error' });
            else enqueueSnackbar(error.message || 'Something went wrong', { variant: 'error' });
        }
    };

    useEffect(() => {
        fetchPositionsMapped();
    }, [crPolicyId, controller.page, controller.pageSize]);

    useEffect(() => {
        if (updatePolicyMappings) {
            const query = new URLSearchParams({});
            query.append('offset', controller.page * controller.pageSize);
            query.append('max', controller.pageSize);
            getPositions(query).unwrap();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updatePolicyMappings, controller.page, controller.pageSize]);

    useEffect(() => {
        if (updatePolicyMappings) {
            const event = { target: { checked: true } };
            positionsMapped?.mappedPositions?.forEach((item) => {
                handleSelect(event, item.position.id);
            });
        }
    }, [updatePolicyMappings, positionsMapped?.mappedPositions]);

    const handlePageChange = (newPage) => {
        dispatch(onPageChange(newPage - 1));
    };

    const handlePageSizeChange = (newPageSize) => {
        dispatch(onPageSizeChange(newPageSize));
    };

    const handleSortChange = (event, property) => {
        const isAsc = controller.sortBy === property && controller.sort === 'asc';
        dispatch(
            onSortChange({
                sort: isAsc ? 'desc' : 'asc',
                sortBy: property,
            }),
        );
    };

    useEffect(() => {
        dispatch(resetController());
    }, [updatePolicyMappings]);

    const handelBulkUpdate = async () => {
        try {
            if (!selectedPositions.length) {
                setUpdatePolicyMappings(true);
                return;
            }

            const bodyFormData = new FormData();
            bodyFormData.append('changeRequestPolicyId', Number(crPolicyId));
            bodyFormData.append('positionIds', selectedPositions.join());

            const response = await bulkUpdatePositionChangeRequestPolicyMappings(bodyFormData).unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);

            fetchPositionsMapped();
            enqueueSnackbar('Position mappings successfully updated', {
                variant: 'success',
            });
            handleClearSelected();
            setUpdatePolicyMappings(false);
        } catch (error) {
            if (error.status === 404) enqueueSnackbar(error.data.message, { variant: 'error' });
            enqueueSnackbar(error.message || 'Something went wrong', { variant: 'error' });
        }
    };

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">Change Policy</Typography>
            </Box>
            <Box sx={styles.contentBodyWrapper}>
                <Card>
                    <CardContent>
                        <Grid container direction="column" spacing={2}>
                            <Grid item>
                                <Button
                                    startIcon={<ChevronLeftIcon />}
                                    variant="text"
                                    color="secondary"
                                    onClick={() => navigate(-1)}
                                >
                                    Back
                                </Button>
                            </Grid>
                            <Grid item>
                                {' '}
                                <Box sx={styles.mappedBox}>
                                    <Typography
                                        sx={styles.contentText}
                                    >{`Policy Name: ${positionsMapped?.name || ''}`}</Typography>
                                    <Typography
                                        sx={styles.contentText}
                                    >{`Mapped Positions: ${positionsMapped?.totalCount || 0}`}</Typography>
                                </Box>
                            </Grid>
                            <Grid item>
                                {!isLoadingPositions && !isLoadingPositionsMapped && (
                                    <CRPoliciesSaveMappings
                                        selectedPositions={selectedPositions}
                                        viewMappedPositionsShow={true}
                                        updatePolicyMappings={updatePolicyMappings}
                                        handelBulkUpdate={handelBulkUpdate}
                                    />
                                )}
                                <CustomTable
                                    isLoading={isLoadingPositions || isLoadingPositionsMapped}
                                    onPageChange={handlePageChange}
                                    onPageSizeChange={handlePageSizeChange}
                                    onSelect={handleSelect}
                                    onSelectAll={handleSelectAll}
                                    onSortChange={handleSortChange}
                                    page={controller.page + 1}
                                    columns={updatePolicyMappings ? positionsColumns : mappedPositionColumns}
                                    rows={
                                        (updatePolicyMappings
                                            ? listPositions?.positionInstanceList
                                            : positionsMapped?.mappedPositions) || []
                                    }
                                    rowsCount={
                                        (updatePolicyMappings
                                            ? listPositions?.positionInstanceCount
                                            : positionsMapped?.totalCount) || 0
                                    }
                                    selectedRows={selectedPositions}
                                    sort={controller.sort}
                                    sortBy={controller.sortBy}
                                    showRowSelector={updatePolicyMappings}
                                    showPagignation
                                    keyColumn="id"
                                    route="changerequest/crpolicies"
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default ViewUpdateMappedPositions;
